import {
  WEEK_ORDER,
  WEEKDAY_LONG,
  WEEKDAY_PLURAL,
  WEEKDAY_SHORT,
  changeCount,
  copyMonthDraft,
  countSessions,
  draftFromEditor,
  draftGroups,
  formatDateKey,
  itemDates,
  lastCountedDate,
  listWords,
  localTime,
  minutesOf,
  monthHolidayClosures,
  monthRange,
  planNextMonthDue,
  publishFromDraft,
  removedItems,
  setItemDayStaff,
  uaePublicHoliday,
  type DraftItem,
  type PublishTimetableResult,
  type TimetableDraft,
  type TimetableEditor,
} from "@teamna/shared";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useLayoutEffect, useReducer, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { track } from "@teamna/web-client/analytics/web.ts";
import { ApiError } from "@teamna/web-client/api/client.ts";
import { useCreateGroup } from "@teamna/web-client/hooks/use-groups.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useActiveBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { usePublishTimetable, useTimetableEditor, useTimetablePreview } from "@teamna/web-client/hooks/use-timetable.ts";
import { Button, Callout, ErrorState, SegmentedControl, SkeletonList, showToast } from "../../components/index.ts";
import { DESKTOP_QUERY, useMediaQuery } from "../../hooks/use-media-query.ts";
import { GROUP_PALETTE, groupColor } from "../../lib/group-color.ts";
import { CoachStrip } from "./CoachStrip.tsx";
import { DatesBoard } from "./DatesBoard.tsx";
import { MonthCheck, MonthStart } from "./MonthPanels.tsx";
import {
  CARD_EDGE,
  bookedIndex,
  bookedTotal,
  dayRange,
  editorProblems,
  fitCard,
  levelCoaches,
  offersFor,
  sessionsChange,
  timeLabel,
  type CardSpot,
  type EditorProblem,
} from "./model.ts";
import { AddLevelForm, DayPanel, ItemEditor, LevelList, RemovedItem, type NewLevel, type RailWords } from "./Rail.tsx";
import { ReviewPanel, reviewLines } from "./ReviewPanel.tsx";
import { initialState, reducer, saveDraft, savedDraft } from "./state.ts";
import { WeekBoard, type BoardGroup } from "./WeekBoard.tsx";

/**
 * The owner timetable (prototype `teamna/prototypes/owner-timetable.html`, approved 2026-10-09).
 *
 * One week editor for one month (this month, or the next once this month is published): pick a level and click the week (the level stays picked, and
 * "Next level" goes on to the next level with no times; prototype `timetable-flow-v2.html`, approved
 * 2026-10-17), open a block in the card next to the week, drag a block to move it,
 * pull its edge to change the length, paint each person's free hours for private sessions, close
 * dates on the Month view. One month at a time (spec `teamna/strategy/timetable-months.md`, decided
 * 2026-10-17): this month, or the next once this month is published; an empty month starts from a
 * copy of the month before or empty. Everything stays a draft in this browser until
 * Publish. Problems (a person in two places, a limit under the people booked, private hours with no
 * one to run them) show in a note with one-click fixes, and Publish waits until none is left. Undo
 * (or Ctrl or Cmd + Z) goes back one step at a time.
 *
 * The Timetable view of the Schedule page (`../schedule.tsx`), which draws the page title and the
 * switch between the two views. 1024px and up only: below that it says to open it on a computer.
 */
export function TimetableView() {
  const { business, isLoading, error } = useActiveBusiness();
  const desktop = useMediaQuery(DESKTOP_QUERY);
  const businessId = business?.businessId ?? null;
  // The month on screen is in the address (`?view=timetable&month=2026-12`), so the Home To do can open it.
  const search = useSearch({ strict: false }) as { month?: string };
  const navigate = useNavigate();
  const month = /^\d{4}-(0[1-9]|1[0-2])$/.test(search.month ?? "") ? search.month! : null;
  const editor = useTimetableEditor(businessId, month);
  const openMonth = (next: string) => void navigate({ to: ".", search: { view: "timetable", month: next } as never });

  if (!desktop) {
    return (
      <Callout tone="quiet" title="Change the schedule on a computer">
        The schedule editor needs a wider screen. Open Teamna on a computer to change the weekly times. The sessions of the week are here.
      </Callout>
    );
  }
  if (isLoading || editor.isLoading) return <SkeletonList />;
  if (error || editor.error || !business || !editor.data) return <ErrorState error={error ?? editor.error} />;
  return (
    <TimetableEditorView
      key={`${business.businessId}|${editor.data.month ?? "now"}`}
      businessId={business.businessId}
      businessName={business.name}
      editor={editor.data}
      onMonth={openMonth}
    />
  );
}

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** "December 2026" */
function monthLabel(month: string): string {
  const [year, number] = month.split("-").map(Number) as [number, number];
  return `${MONTH_NAMES[number - 1]!} ${year}`;
}

/** "December" */
function monthName(month: string): string {
  return MONTH_NAMES[Number(month.slice(5, 7)) - 1]!;
}

function TimetableEditorView({
  businessId,
  businessName,
  editor,
  onMonth,
}: {
  businessId: string;
  businessName: string;
  editor: TimetableEditor;
  onMonth: (month: string) => void;
}) {
  const { label } = useLabels();
  const loaded = draftFromEditor(editor);
  // One month (older servers: this month, published). Drafts are kept per month.
  const month = editor.month ?? editor.today.slice(0, 7);
  const monthState = editor.monthState ?? "published";
  const months = editor.months ?? [];
  const range0 = monthRange(month);
  const draftKey = `${businessId}:${month}`;
  // Now in the business time zone, read once: the week opens at this hour.
  const [nowMinute] = useState(() => minutesOf(localTime(new Date(), editor.timezone)));
  const stamp = JSON.stringify(loaded);
  const [state, dispatch] = useReducer(reducer, null, () => initialState(stamp, loaded, month, savedDraft(draftKey, stamp, loaded)));
  // An empty month: the owner chose "Start empty" (a copy shows in the draft itself).
  const [startedEmpty, setStartedEmpty] = useState(false);
  // The coach row: a face under the pointer, or one clicked to keep, shows only that person's blocks.
  const [hoverCoach, setHoverCoach] = useState<string | null>(null);
  const [pinnedCoach, setPinnedCoach] = useState<string | null>(null);
  /** Where the owner dragged the time card to. It stays there for the next time they open. */
  const [cardSpot, setCardSpot] = useState<CardSpot | null>(null);
  // One key per draft, kept through an error, so a second press of Publish replays the first.
  const [publishKey, setPublishKey] = useState<{ fingerprint: string; key: string } | null>(null);
  const [published, setPublished] = useState<{ summary: PublishTimetableResult["summary"]; sessions: number; until: string } | null>(null);
  const publish = usePublishTimetable(businessId, month);
  const createGroup = useCreateGroup(businessId);

  // A new published timetable (after a publish, or from another tab) starts a new draft from it.
  // The owner chose its end date there, so a "No end date" stays as it is.
  if (state.stamp !== stamp) {
    dispatch({ type: "load", stamp, base: loaded, month, restore: savedDraft(draftKey, stamp, loaded) });
  }

  useEffect(() => {
    saveDraft(draftKey, state);
  }, [draftKey, state]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      // Esc closes the open block, else stops adding times of a level.
      if (event.key === "Escape") {
        dispatch({ type: "escape" });
        setPinnedCoach(null);
        return;
      }
      if (!(event.metaKey || event.ctrlKey) || event.shiftKey || event.key.toLowerCase() !== "z") return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "SELECT" || target.tagName === "TEXTAREA")) return;
      event.preventDefault();
      dispatch({ type: "undo" });
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const words: RailWords = {
    group: label("group"),
    groupPlural: label("group", { plural: true }),
    staff: label("staff"),
    staffPlural: label("staff", { plural: true }),
    people: label("participant", { plural: true }),
    // Who books: a family books for children; where people book for themselves, the member word.
    family: label("member") === label("participant") ? label("member") : "family",
    families: label("member") === label("participant") ? label("member", { plural: true }) : "families",
  };
  const team = editor.team;
  const personName = (userId: string) => team.find((member) => member.userId === userId)?.name ?? "Team member";
  const groupList = draftGroups(editor);
  const boardGroups: BoardGroup[] = editor.groups.map((group) => ({
    id: group.id,
    name: group.name,
    color: groupColor(group.color, group.colorIndex),
    kind: group.kind,
    durationMin: group.durationMin,
    staffUserIds: group.staffUserIds,
  }));
  const groupsById = new Map(boardGroups.map((group) => [group.id, group]));
  const booked = bookedIndex(editor);
  // Sessions count from today in this month, or from the first day of the next month.
  const context = { today: range0.first > editor.today ? range0.first : editor.today, horizonDays: editor.horizonDays, daysOff: editor.daysOff };
  const { draft, base } = state;
  const lastDate = lastCountedDate(draft, context);
  const untilLabel = (until: string | null) => (until === null ? "no end date" : formatDateKey(until));
  const changes = changeCount(base, draft);
  const sessionCounts = {
    from: context.today > editor.today ? formatDateKey(context.today) : "today",
    now: countSessions(base, context),
    nowUntil: formatDateKey(lastCountedDate(base, context)),
    after: countSessions(draft, context),
    afterUntil: formatDateKey(lastDate),
  };
  const sessionsDelta = sessionCounts.after - sessionCounts.now;
  const problems = editorProblems({
    draft,
    groups: groupList,
    daysOff: editor.daysOff,
    booked,
    words: { groupName: (id) => groupsById.get(id)?.name ?? "A level", personName, staff: words.staff, people: words.people },
    touched: state.touched,
  });
  const flagged = new Set(problems.flatMap((problem) => problem.items));
  // A copy of the month before: its items name the rows they came from.
  const copied = draft.items.some((item) => item.from && Object.keys(item.from).length > 0);
  const request = (() => {
    const { slots, closures } = publishFromDraft(draft, groupList);
    return { slots, closures, source: copied ? ("month_copy" as const) : ("editor" as const) };
  })();
  const preview = useTimetablePreview(businessId, month, changes > 0 && !state.dragging ? request : null);
  // Row-aware: a block whose days all moved to another coach's block is not removed (no ghost).
  const removed = removedItems(base, draft);
  // An empty month opens on the hours of the month before, so its dashed times are in view.
  const previousItems = editor.previous ? draftFromEditor({ groups: editor.previous.groups, until: null, closures: [] }).items : [];
  const range = dayRange([...draft.items, ...removed, ...(monthState === "none" ? previousItems : [])]);

  function edit(labelText: string, change: (current: TimetableDraft) => TimetableDraft, extra: { touched?: string; select?: string | null; last?: string } = {}) {
    dispatch({ type: "edit", label: labelText, change, ...extra });
  }

  function replaceItem(labelText: string, next: DraftItem) {
    edit(labelText, (current) => ({ ...current, items: current.items.map((item) => (item.key === next.key ? next : item)) }), { touched: next.key });
  }

  /** A published time stays open to put back; a new one is gone. */
  function removeItem(key: string) {
    edit("the removal", (current) => ({
      ...current,
      items: current.items.filter((item) => item.key !== key),
      closures: current.closures.filter((row) => row.item !== key),
    }), { select: base.items.some((item) => item.key === key) ? key : null });
  }

  function addAt(weekday: number, minute: number) {
    const brush = state.brush;
    const group = brush ? groupsById.get(brush.groupId) : undefined;
    const source = brush ? editor.groups.find((row) => row.id === brush.groupId) : undefined;
    if (!brush || !group || !source) return;
    const key = `new-${crypto.randomUUID()}`;
    const free = group.kind === "private";
    const length = free ? group.durationMin * Math.max(1, Math.round(120 / group.durationMin)) : group.durationMin;
    const start = Math.max(0, Math.min(24 * 60 - length, minute));
    const item: DraftItem = {
      key,
      groupId: group.id,
      free,
      days: [weekday],
      start,
      end: start + length,
      capacity: free ? 1 : source.capacity,
      // A level with several coaches: the coach picked in the rail. One coach: the level's.
      staffUserId: free ? brush.staffUserId : (brush.staffUserId ?? source.staffUserId),
      ids: {},
    };
    // The level stays picked for the next time. The other days show this one dashed: one click adds a day.
    edit("the new time", (current) => ({ ...current, items: [...current.items, item] }), { touched: key, select: null, last: key });
    showToast(
      free
        ? `${item.staffUserId ? personName(item.staffUserId) : "No one yet"} is free ${timeLabel(item.start)} to ${timeLabel(item.end)} every ${WEEKDAY_LONG[weekday]}. Click a dashed box to add another day.`
        : `${group.name} at ${timeLabel(item.start)} every ${WEEKDAY_LONG[weekday]} until ${formatDateKey(lastDate)}. Click a dashed box to add the same time on another day.`,
      { tone: "info" },
    );
  }

  function dropped(item: DraftItem, origin: DraftItem, mode: "move" | "resize") {
    const changed = item.start !== origin.start || item.end !== origin.end || item.days.join() !== origin.days.join();
    dispatch({ type: "drag-end", changed });
    if (!changed) return;
    const group = groupsById.get(item.groupId);
    const name = item.free ? `${item.staffUserId ? personName(item.staffUserId) : "No one yet"}'s free hours` : (group?.name ?? "The level");
    const on = listWords([...item.days].sort((a, b) => a - b).map((day) => WEEKDAY_SHORT[day]!));
    if (mode === "resize") {
      showToast(item.free ? `${name} now end at ${timeLabel(item.end)} on ${on}.` : `${name} is now ${item.end - item.start} minutes on ${on}.`, { tone: "info" });
      return;
    }
    const before = base.items.find((row) => row.key === item.key);
    const kept = before ? before.days.filter((day) => item.days.includes(day)) : [];
    const lost = before ? before.days.filter((day) => !item.days.includes(day)) : [];
    const moving = before && !item.free && before.start !== item.start ? bookedTotal(before, booked, kept) : 0;
    const back = before ? bookedTotal(before, booked, lost) : 0;
    showToast(
      `${name} now at ${timeLabel(item.start)}${item.free ? ` to ${timeLabel(item.end)}` : ""} on ${on}.` +
        (moving ? ` ${moving} ${moving === 1 ? "booking moves" : "bookings move"} with it.` : "") +
        (back ? ` ${listWords(lost.map((day) => WEEKDAY_SHORT[day]!))} bookings (${back}) get their session back.` : ""),
      { tone: "info" },
    );
  }

  function saveLevel(level: NewLevel) {
    createGroup.mutate(
      {
        name: level.name.trim(),
        durationMin: level.durationMin,
        defaultCapacity: level.kind === "private" ? 1 : level.capacity,
        bookingModel: "open",
        kind: level.kind,
        staffUserIds: level.kind === "private" ? [] : level.staffUserIds,
        color: GROUP_PALETTE[editor.groups.length % GROUP_PALETTE.length],
      },
      {
        onSuccess: (group) => {
          dispatch({ type: "adding", adding: false });
          dispatch({
            type: "brush",
            brush: {
              groupId: group.id,
              staffUserId: level.kind === "private" ? (team[0]?.userId ?? null) : (level.staffUserIds[0] ?? null),
            },
          });
          showToast(`${group.name} is added. Click the week to give it times.`, { tone: "success" });
        },
      },
    );
  }

  function doPublish() {
    const fingerprint = JSON.stringify(request);
    const key = publishKey?.fingerprint === fingerprint ? publishKey.key : crypto.randomUUID();
    setPublishKey({ fingerprint, key });
    publish.mutate({ request, idempotencyKey: key }, {
      onSuccess: (result) => {
        setPublishKey(null);
        setPublished({ summary: result.summary, sessions: countSessions(draft, context), until: draft.until ? formatDateKey(draft.until) : `${formatDateKey(lastDate)}, and more each night` });
      },
      onError: (failure) => {
        const detail =
          failure instanceof ApiError && failure.code === "TIMETABLE_PROBLEMS"
            ? ((failure.meta?.problems as Array<{ detail: string }> | undefined)?.[0]?.detail ?? failure.detail)
            : failure instanceof ApiError
              ? failure.detail
              : "The schedule did not publish. Try again.";
        showToast(detail, { tone: "info" });
      },
    });
  }

  // An empty month: the month before, dashed on the week, until the owner copies it or starts empty.
  const previousDraft = editor.previous ? draftFromEditor({ groups: editor.previous.groups, until: null, closures: [] }) : null;
  const choosing =
    monthState === "none" && previousDraft !== null && previousDraft.items.length > 0 && draft.items.length === 0 && state.history.length === 0 && !startedEmpty;
  const nextMonth = months[1];
  const thisMonth = months[0];
  const monthOffset = month === thisMonth?.month ? 0 : 1;

  function copyPrevious() {
    if (!previousDraft || !editor.previous) return;
    const next = copyMonthDraft(previousDraft, month, editor.daysOff, () => `new-${crypto.randomUUID()}`);
    edit(`the copy of ${monthName(editor.previous.month)}`, () => next);
    dispatch({ type: "view", view: "week" });
    track("timetable_month_started", { choice: "copy", month_offset: monthOffset });
    const holidays = next.closures.length;
    showToast(
      `${monthName(editor.previous.month)} is copied to ${monthName(month)}.${holidays ? ` ${holidays} holiday ${holidays === 1 ? "date is" : "dates are"} closed.` : ""} Check, then publish.`,
      { tone: "info" },
    );
  }

  function startEmpty() {
    setStartedEmpty(true);
    track("timetable_month_started", { choice: "empty", month_offset: monthOffset });
    showToast(`${monthName(month)} starts empty. Pick a ${words.group}, then click the week.`, { tone: "info" });
  }

  // The holidays of the month after a copy: closed by default, the owner can open each one.
  const holidays = monthState === "none" && copied
    ? monthHolidayClosures(month, editor.daysOff).map((holiday) => ({
        ...holiday,
        closed: draft.closures.some((row) => row.date === holiday.date && row.item === null),
      }))
    : [];
  function setHoliday(date: string, closed: boolean) {
    edit(closed ? "the closed day" : "the opened day", (current) => ({
      ...current,
      closures: closed
        ? [...current.closures.filter((row) => !(row.date === date && row.item === null)), { date, item: null, reason: uaePublicHoliday(date) }]
        : current.closures.filter((row) => !(row.date === date && row.item === null)),
    }));
  }

  // The end of this month is near and the next month has nothing planned: say so, with the way there.
  const endingSoon =
    month === thisMonth?.month &&
    thisMonth.state === "published" &&
    nextMonth?.state === "none" &&
    editor.lastSession !== undefined &&
    planNextMonthDue(editor.today, editor.lastSession);

  const selectedItem = state.selected ? draft.items.find((item) => item.key === state.selected) : undefined;
  const selectedRemoved = state.selected ? removed.find((item) => item.key === state.selected) : undefined;
  const firstProblem: EditorProblem | undefined = problems[0];
  const offers = firstProblem
    ? offersFor(firstProblem, draft, team, { start: range.start, end: range.end }, (groupId) => {
        const group = groupsById.get(groupId);
        return group ? levelCoaches(group, draft) : [];
      })
    : [];
  const lines = reviewLines({
    base,
    draft,
    groups: groupsById,
    booked,
    personName,
    businessName,
    sessionsOf: (item) => itemDates(item, draft, context).length,
    untilLabel: (until) => untilLabel(until),
    words,
  });

  // The level a click on the week adds, and the weekly time whose other days show dashed: the open
  // block, or else the time just added with the picked level.
  const brushGroup = state.brush ? groupsById.get(state.brush.groupId) : undefined;
  const lastAdded = state.brush && state.last ? draft.items.find((item) => item.key === state.last && item.groupId === state.brush?.groupId) : undefined;
  const hint = selectedItem ?? lastAdded ?? null;

  function firstBrush(group: BoardGroup) {
    return { groupId: group.id, staffUserId: group.kind === "private" ? (team[0]?.userId ?? null) : (levelCoaches(group, draft)[0] ?? null) };
  }

  function pickLevel(brush: { groupId: string; staffUserId: string | null } | null, via: "list" | "next_button") {
    const group = brush ? groupsById.get(brush.groupId) : undefined;
    if (group && brush?.groupId !== state.brush?.groupId) {
      track("timetable_level_picked", {
        via,
        level_kind: group.kind,
        level_count: boardGroups.length,
        levels_with_times: boardGroups.filter((row) => draft.items.some((item) => item.groupId === row.id)).length,
      });
    }
    dispatch({ type: "brush", brush });
  }

  let rail;
  if (choosing && previousDraft && editor.previous) {
    const levels = boardGroups
      .map((group) => ({
        id: group.id,
        name: group.name,
        color: group.color,
        weekly: previousDraft.items.filter((item) => item.groupId === group.id).reduce((total, item) => total + item.days.length, 0),
      }))
      .filter((level) => level.weekly > 0);
    rail = (
      <MonthStart
        words={{ families: words.families, group: words.group }}
        monthName={monthName(month)}
        previousName={monthName(editor.previous.month)}
        weeklyTimes={previousDraft.items.reduce((total, item) => total + item.days.length, 0)}
        levels={levels}
        sessions={countSessions(copyMonthDraft(previousDraft, month, editor.daysOff, () => "count"), context)}
        onCopy={copyPrevious}
        onEmpty={startEmpty}
      />
    );
  } else if (state.view === "dates" && state.day) {
    const date = state.day;
    rail = (
      <DayPanel
        date={date}
        draft={draft}
        groups={groupsById}
        booked={booked}
        personName={personName}
        onBack={() => dispatch({ type: "day", day: null })}
        onClose={(closed) =>
          edit(closed ? "the closed day" : "the opened day", (current) => ({
            ...current,
            closures: closed
              ? [...current.closures.filter((row) => !(row.date === date && row.item === null)), { date, item: null, reason: uaePublicHoliday(date) }]
              : current.closures.filter((row) => !(row.date === date && row.item === null)),
          }))
        }
        onSkip={(itemKey, off) =>
          edit(off ? "the session taken off" : "the session put back", (current) => ({
            ...current,
            closures: off
              ? [...current.closures, { date, item: itemKey, reason: null }]
              : current.closures.filter((row) => !(row.date === date && row.item === itemKey)),
          }))
        }
      />
    );
  } else if (state.adding) {
    rail = (
      <AddLevelForm
        team={team}
        words={words}
        saving={createGroup.isPending}
        error={createGroup.error instanceof ApiError ? createGroup.error.detail : createGroup.error ? "The level was not added. Try again." : null}
        onCancel={() => dispatch({ type: "adding", adding: false })}
        onSave={saveLevel}
      />
    );
  } else {
    rail = (
      <>
      {holidays.length > 0 || (monthState === "none" && copied && editor.previous) ? (
        <MonthCheck
          monthName={monthName(month)}
          previousName={editor.previous ? monthName(editor.previous.month) : "last month"}
          holidays={holidays}
          sessions={countSessions(draft, context)}
          onHoliday={setHoliday}
        />
      ) : null}
      <LevelList
        groups={boardGroups}
        draft={draft}
        brush={state.brush}
        team={team}
        words={words}
        until={formatDateKey(lastDate)}
        canReview={changes > 0}
        personName={personName}
        onBrush={(brush) => pickLevel(brush, "list")}
        onNext={(group) => {
          pickLevel(firstBrush(group), "next_button");
          showToast(`${group.name} is picked. Click the week to add its times.`, { tone: "info" });
        }}
        onOpen={(key) => dispatch({ type: "select", key })}
        onAdd={() => dispatch({ type: "adding", adding: true })}
        onReview={() => dispatch({ type: "review", open: true })}
      />
      </>
    );
  }

  // A block opens in a card on the week, on the side away from the block, so the levels stay on the rail.
  let card = null;
  if (state.view === "week" && selectedItem) {
    const group = groupsById.get(selectedItem.groupId);
    card = group ? (
      <ItemEditor
        key={selectedItem.key}
        item={selectedItem}
        before={base.items.find((item) => item.key === selectedItem.key) ?? null}
        group={group}
        coaches={levelCoaches(group, draft)}
        team={team}
        words={words}
        booked={booked}
        daysOff={editor.daysOff}
        sessionsUntil={`${itemDates(selectedItem, draft, context).length} sessions until ${formatDateKey(lastDate)}`}
        onClose={() => dispatch({ type: "select", key: null })}
        onChange={replaceItem}
        siblings={draft.items.filter(
          (item) => item.key !== selectedItem.key && !item.free && item.groupId === selectedItem.groupId && item.start === selectedItem.start,
        )}
        onSelect={(key) => dispatch({ type: "select", key })}
        onDayStaff={(key, weekday, staffUserId) => {
          const holder = draft.items.find((item) => item.key === key);
          if (!holder) return;
          const newKey = `new-${crypto.randomUUID()}`;
          const result = setItemDayStaff(draft, key, weekday, staffUserId, newKey);
          if (result.draft === draft) return;
          // Stay on this block while it still has days; else follow the day.
          const stays = result.draft.items.some((item) => item.key === selectedItem.key);
          edit(`the ${words.staff} change`, (current) => setItemDayStaff(current, key, weekday, staffUserId, newKey).draft, {
            touched: result.key,
            select: stays ? selectedItem.key : result.key,
          });
          if (result.key !== key) {
            showToast(
              `${WEEKDAY_PLURAL[weekday]} at ${timeLabel(holder.start)} are now with ${staffUserId ? personName(staffUserId) : `no ${words.staff}`}.`,
              { tone: "info" },
            );
          }
        }}
        onUndo={() => {
          const before = base.items.find((item) => item.key === selectedItem.key);
          if (before) replaceItem("Undo changes", before);
        }}
        onRemove={() => removeItem(selectedItem.key)}
      />
    ) : null;
  } else if (state.view === "week" && selectedRemoved) {
    const group = groupsById.get(selectedRemoved.groupId);
    card = group ? (
      <RemovedItem
        item={selectedRemoved}
        group={group}
        onClose={() => dispatch({ type: "select", key: null })}
        onRestore={() => edit("Put it back", (current) => ({ ...current, items: [...current.items, selectedRemoved] }), { select: selectedRemoved.key })}
      />
    ) : null;
  }
  const cardItem = selectedItem ?? selectedRemoved;
  const cardOnLeft = cardItem ? Math.min(...cardItem.days.map((day) => WEEK_ORDER.indexOf(day))) >= 4 : false;

  const reviewOpen = state.review || published !== null;

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <fieldset className="m-0 flex items-center gap-1 border-0 p-0">
          <legend className="sr-only">Month</legend>
          <Button
            variant="secondary"
            size="sm"
            aria-label="This month"
            title="This month"
            disabled={!thisMonth || month === thisMonth.month}
            onClick={() => thisMonth && onMonth(thisMonth.month)}
            className="px-2.5"
          >
            ‹
          </Button>
          <b className="min-w-[132px] text-center text-[15px]">{monthLabel(month)}</b>
          <Button
            variant="secondary"
            size="sm"
            aria-label="Next month"
            title={nextMonth && !nextMonth.canOpen ? "Publish this month first" : "Next month"}
            disabled={!nextMonth || month === nextMonth.month || !nextMonth.canOpen}
            onClick={() => nextMonth && onMonth(nextMonth.month)}
            className="px-2.5"
          >
            ›
          </Button>
        </fieldset>
        <span
          className={[
            "rounded-full px-2.5 py-0.5 text-[11.5px] font-bold",
            changes > 0 ? "bg-late-surface text-late" : monthState === "published" ? "bg-confirmed-surface text-confirmed" : "bg-sunken text-ink-2",
          ].join(" ")}
        >
          {changes > 0 ? "Draft" : monthState === "published" ? "Published" : "No sessions"}
        </span>
        <p className="text-[13px] text-ink-2">{`Sessions from ${formatDateKey(context.today)} to ${formatDateKey(range0.last)}.`}</p>
        <div className="ml-auto flex flex-wrap items-center gap-3">
          <SegmentedControl
            label="View"
            size="compact"
            value={state.view}
            options={[
              { value: "week", label: "Every week" },
              { value: "dates", label: "Month" },
            ]}
            onChange={(view) => dispatch({ type: "view", view })}
          />
        </div>
      </div>

      {endingSoon && nextMonth ? (
        <div className="flex flex-wrap items-center gap-3 rounded-card border border-late/40 bg-late-surface px-3.5 py-2.5 text-[13px] font-semibold text-late">
          <span className="grid min-w-0 flex-1 gap-0.5">
            {editor.lastSession! < editor.today ? "The last session was on" : `${monthName(month)} sessions end on`} {formatDateKey(editor.lastSession!)}.
            <small className="font-medium text-ink-2">
              {words.families.charAt(0).toUpperCase()}
              {words.families.slice(1)} cannot book {monthName(nextMonth.month)} yet. Plan it now.
            </small>
          </span>
          <Button size="sm" onClick={() => onMonth(nextMonth.month)}>
            Plan {monthName(nextMonth.month)} →
          </Button>
        </div>
      ) : null}

      <div className="relative grid h-[calc(100vh-252px)] min-h-[560px] grid-cols-[272px_minmax(0,1fr)] grid-rows-[minmax(0,1fr)] overflow-clip rounded-card border border-line bg-paper shadow-card">
        <aside aria-label="Schedule tools" className="grid content-start gap-3 overflow-y-auto border-r border-line p-3.5">
          {rail}
        </aside>
        <div className="flex min-h-0 min-w-0 flex-col">
          {state.view === "week" ? (
            <CoachStrip
              items={draft.items.map((item) => ({ staffUserId: item.staffUserId, sessions: item.days.length }))}
              team={team}
              staffPlural={words.staffPlural}
              highlight={hoverCoach ?? pinnedCoach}
              pinned={pinnedCoach}
              onHover={setHoverCoach}
              onPin={setPinnedCoach}
            />
          ) : null}
          {state.view === "week" && state.brush && brushGroup ? (
            <div className="flex items-center gap-2.5 border-b border-line bg-accent-surface px-3 py-1 text-[12.5px] font-semibold text-accent-strong">
              <i className="size-2.5 shrink-0 rounded-[3px]" style={{ background: brushGroup.color }} aria-hidden="true" />
              <span className="min-w-0 flex-1">
                {brushGroup.kind === "private" ? (
                  `A click on the week adds free hours for ${state.brush.staffUserId ? personName(state.brush.staffUserId) : "no one yet"}.`
                ) : (
                  <>
                    A click on the week adds <b>{brushGroup.name}</b>, {brushGroup.durationMin} min
                    {state.brush.staffUserId ? `, ${personName(state.brush.staffUserId)}` : ""}.
                  </>
                )}{" "}
                Esc stops.
              </span>
              <Button variant="ghost" size="sm" onClick={() => dispatch({ type: "brush", brush: null })}>
                Stop adding
              </Button>
            </div>
          ) : null}
          <div className="relative min-h-0 flex-1">
          {firstProblem && !reviewOpen ? (
            <output
              className={[
                card && !cardSpot ? (cardOnLeft ? "right-3 left-[324px]" : "right-[324px] left-3") : "inset-x-3",
                "absolute z-30 flex items-center gap-2.5 rounded-xl border border-cancelled/40 bg-cancelled-surface px-3 py-2 text-[13px] font-semibold text-cancelled shadow-card",
                state.view === "week" ? "top-14" : "top-3",
              ].join(" ")}
            >
              <span className="grid min-w-0 flex-1 gap-0.5">
                <span>{firstProblem.detail}</span>
                <small className="font-medium text-ink-2">
                  {firstProblem.code === "COACH_CLASH" ? `A ${words.staff} can do one thing at a time.` : "Publish waits until this is fixed."}
                  {problems.length > 1 ? ` ${problems.length - 1} more ${problems.length === 2 ? "problem" : "problems"} after this one.` : ""}
                </small>
              </span>
              <span className="flex shrink-0 gap-1.5">
                {offers.map((offer) => (
                  <Button
                    key={offer.label}
                    variant="danger"
                    size="sm"
                    onClick={() => {
                      const item = draft.items.find((row) => row.key === firstProblem.fix);
                      if (!item) return;
                      replaceItem("the fix from the note", offer.apply(item));
                      showToast(`Done. ${offer.label.replace(/^Use /, "")}.`, { tone: "success" });
                    }}
                    className="whitespace-nowrap"
                  >
                    {offer.label}
                  </Button>
                ))}
              </span>
            </output>
          ) : null}
          <section aria-label={state.view === "week" ? "Every week" : "Month"} className="h-full overflow-auto bg-surface">
            {state.view === "week" ? (
              <WeekBoard
                // An empty month opens on its first dashed time (the board reads this once, when it opens).
                nowMinute={choosing && previousItems.length > 0 ? Math.min(...previousItems.map((item) => item.start)) : nowMinute}
                draft={draft}
                removed={removed}
                placeholders={choosing && previousDraft ? previousDraft.items : []}
                placeholderWords={editor.previous ? `in ${monthName(editor.previous.month)}` : ""}
                groups={groupsById}
                personName={personName}
                booked={booked}
                daysOff={editor.daysOff}
                brush={state.brush}
                selected={state.selected}
                hint={hint}
                flagged={flagged}
                usualHours={range}
                onAdd={addAt}
                onSelect={(key) => dispatch({ type: "select", key })}
                onDragStart={(key, labelText) => dispatch({ type: "drag-start", key, label: labelText })}
                onDragMove={(item) => dispatch({ type: "drag-move", item })}
                onDragEnd={dropped}
                onDayOn={(weekday) => {
                  if (!hint) return;
                  replaceItem("the added day", { ...hint, days: [...hint.days, weekday] });
                }}
                onBackground={() => dispatch({ type: "select", key: null })}
                highlight={hoverCoach ?? pinnedCoach}
                onDayOff={(weekday) => {
                  if (!selectedItem) return;
                  if (selectedItem.days.length < 2) removeItem(selectedItem.key);
                  else replaceItem("the day taken off", { ...selectedItem, days: selectedItem.days.filter((day) => day !== weekday) });
                }}
              />
            ) : (
              <DatesBoard
                draft={draft}
                groups={groupsById}
                today={editor.today}
                lastDate={lastDate}
                firstMonth={month}
                lastMonth={month}
                month={month}
                daysOff={editor.daysOff}
                selected={state.day}
                sessionCount={countSessions(draft, context)}
                onMonth={onMonth}
                onPick={(date) => dispatch({ type: "day", day: date })}
              />
            )}
          </section>
          {/* Not during a drag: the card would sit over the days and catch the pointer (the board finds the day under it). */}
          {card && cardItem && !state.dragging ? (
            <TimeCard key={cardItem.key} itemKey={cardItem.key} groupId={cardItem.groupId} onLeft={cardOnLeft} spot={cardSpot} onMove={setCardSpot}>
              {card}
            </TimeCard>
          ) : null}
          </div>
        </div>

        {reviewOpen ? (
          <>
            <button
              type="button"
              aria-label="Close the review"
              className="absolute inset-0 z-40 bg-ink/40"
              onClick={() => {
                setPublished(null);
                dispatch({ type: "review", open: false });
              }}
            />
            <dialog open aria-label={published ? "Published" : "Review and publish"} className="absolute inset-y-0 right-0 left-auto z-50 m-0 h-full w-[470px] max-w-full border-0 bg-paper p-0 text-ink shadow-card">
              <ReviewPanel
                lines={lines}
                problems={problems}
                preview={preview.data}
                previewLoading={preview.isFetching}
                sessionsDelta={sessionsDelta}
                sessionCounts={sessionCounts}
                businessName={businessName}
                publishing={publish.isPending}
                published={published}
                words={words}
                onBack={() => {
                  setPublished(null);
                  dispatch({ type: "review", open: false });
                }}
                onPublish={doPublish}
              />
            </dialog>
          </>
        ) : null}
      </div>

      <PublishBar
        changes={changes}
        everPublished={base.items.length > 0}
        sessions={countSessions(draft, context)}
        lastDate={formatDateKey(lastDate)}
        sessionsDelta={sessionsDelta}
        preview={preview.data}
        problems={problems.length}
        undoLabel={state.history[state.history.length - 1]?.label ?? null}
        onUndo={() => {
          const last = state.history[state.history.length - 1];
          dispatch({ type: "undo" });
          if (last) showToast(`Undone: ${last.label}.`, { tone: "info" });
        }}
        onUndoAll={() => {
          dispatch({ type: "undo-all" });
          showToast(`Back to what ${words.families} see.`, { tone: "info" });
        }}
        onReview={() => dispatch({ type: "review", open: true })}
        words={words}
      />
    </div>
  );
}

/**
 * The card a block opens in, on the week. Focus goes into it when it opens, so a keyboard or screen
 * reader user lands on its controls. When it closes with focus inside it (Close, Esc), focus goes
 * back to a block of its weekly time (`data-block` on the board), not to whatever had focus before:
 * a pointer press on a block does not focus it. A new time that was deleted has no block: focus goes
 * to its level on the rail (`data-level`). When the owner closed it by pressing something else
 * (a level on the rail, another block), focus stays there. One card per block: the page keys it by
 * the block, so another block mounts a new card.
 */
/** The default top of the card (`top-14`): below the day names of the week. */
const CARD_TOP = 56;

function TimeCard({
  itemKey,
  groupId,
  onLeft,
  spot,
  onMove,
  children,
}: {
  itemKey: string;
  groupId: string;
  onLeft: boolean;
  /** Where the owner dragged the card to; null keeps it on the side away from the block. */
  spot: CardSpot | null;
  onMove: (spot: CardSpot) => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  const drag = useRef<{ x: number; y: number; spot: CardSpot } | null>(null);
  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
    return () => {
      // The card is gone from the page by now: focus that was inside it fell back to the body.
      const active = document.activeElement;
      if (active && active !== document.body) return;
      const block = [...document.querySelectorAll<HTMLElement>("[data-block]")].find((element) => element.dataset.block === itemKey);
      const level = [...document.querySelectorAll<HTMLElement>("[data-level]")].find((element) => element.dataset.level === groupId);
      // A plain focus scrolls the board or the rail to the target when it is out of view.
      (block ?? level)?.focus();
    };
  }, [itemKey, groupId]);

  // A board made smaller after a drag (a smaller window) moves the card back on it. The kept place
  // does not change, so the card goes back there when the board is large again.
  useLayoutEffect(() => {
    const card = ref.current;
    const board = card?.offsetParent;
    if (!spot || !card || !(board instanceof HTMLElement)) return;
    const fit = () => {
      if (drag.current) return;
      const next = place(card, spot.left, spot.top);
      card.style.left = `${next.left}px`;
      card.style.top = `${next.top}px`;
    };
    fit();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(fit);
    observer.observe(board);
    observer.observe(card);
    return () => observer.disconnect();
  }, [spot]);

  const onPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
    const card = ref.current;
    // A drag starts on the card itself, never on a control in it.
    if (!card || event.button !== 0 || (event.target as Element).closest("button, input, select, textarea, a, label")) return;
    event.preventDefault();
    card.setPointerCapture(event.pointerId);
    drag.current = { x: event.clientX, y: event.clientY, spot: { left: card.offsetLeft, top: card.offsetTop } };
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLElement>) => {
    const card = ref.current;
    if (!card || !drag.current) return;
    // Straight on the element while the pointer moves: a state change on each move would draw the whole timetable again.
    const next = place(card, drag.current.spot.left + event.clientX - drag.current.x, drag.current.spot.top + event.clientY - drag.current.y);
    card.style.left = `${next.left}px`;
    card.style.top = `${next.top}px`;
    card.style.right = "auto";
  };
  const onPointerUp = () => {
    const card = ref.current;
    if (!card || !drag.current) return;
    drag.current = null;
    onMove(place(card, card.offsetLeft, card.offsetTop));
  };

  return (
    <section
      ref={ref}
      tabIndex={-1}
      aria-label="The open time"
      title="Drag the card to move it"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      // One height limit, from the default top, wherever the card is: a card dragged low moves up to fit.
      style={{ ...(spot ? { left: spot.left, top: spot.top } : null), maxHeight: `calc(100% - ${CARD_TOP + CARD_EDGE}px)` }}
      className={[
        "absolute z-[35] grid w-[300px] cursor-grab content-start overflow-y-auto rounded-card border border-line-strong bg-paper p-3.5 pt-2 shadow-card outline-none select-none focus-visible:outline-3 focus-visible:outline-accent-soft active:cursor-grabbing",
        spot ? "" : onLeft ? "top-14 left-3" : "top-14 right-3",
      ].join(" ")}
    >
      <i className="mx-auto mb-1.5 h-1 w-10 rounded-full bg-line-strong" aria-hidden="true" />
      {children}
    </section>
  );
}

/** The card moves on the board, and stays fully on it. */
function place(card: HTMLElement, left: number, top: number): CardSpot {
  const board = card.offsetParent;
  return fitCard(
    { left, top },
    { width: board instanceof HTMLElement ? board.clientWidth : 0, height: board instanceof HTMLElement ? board.clientHeight : 0 },
    { width: card.offsetWidth, height: card.offsetHeight },
  );
}

function PublishBar({
  changes,
  everPublished,
  sessions,
  lastDate,
  sessionsDelta,
  preview,
  problems,
  undoLabel,
  onUndo,
  onUndoAll,
  onReview,
  words,
}: {
  words: RailWords;
  changes: number;
  everPublished: boolean;
  sessions: number;
  lastDate: string;
  sessionsDelta: number;
  preview: PublishTimetableResult | undefined;
  problems: number;
  undoLabel: string | null;
  onUndo: () => void;
  onUndoAll: () => void;
  onReview: () => void;
}) {
  const undo = undoLabel ? (
    <Button variant="secondary" size="sm" onClick={onUndo} title={`Undo ${undoLabel} (Ctrl or Cmd + Z)`} aria-keyshortcuts="Control+Z Meta+Z">
      ↶ Undo
    </Button>
  ) : null;

  if (changes === 0) {
    return (
      <div className="sticky bottom-0 z-20 flex items-center gap-3 rounded-card border border-line bg-paper px-4 py-2.5">
        <span className="grid min-w-0 flex-1 text-[12px] text-ink-2">
          <b className="text-[14px] text-ink">{everPublished ? "✓ Published" : "Not published"}</b>
          {everPublished
            ? `${words.families.charAt(0).toUpperCase()}${words.families.slice(1)} see this schedule. ${sessions} sessions until ${lastDate}.`
            : `${words.families.charAt(0).toUpperCase()}${words.families.slice(1)} see nothing yet.`}
        </span>
        {undo}
      </div>
    );
  }

  const bits: string[] = [];
  if (sessionsDelta) {
    const change = sessionsChange(sessionsDelta);
    bits.push(`${change.value} ${change.label}`);
  }
  if (preview) {
    const touched = preview.summary.bookingsMoved + preview.summary.bookingsReturned;
    if (touched) bits.push(`${touched} ${touched === 1 ? "booking changes" : "bookings change"}`);
    if (preview.summary.parentsTold) bits.push(`${preview.summary.parentsTold} ${preview.summary.parentsTold === 1 ? `${words.family} gets` : `${words.families} get`} a message`);
  }
  if (bits.length === 0) bits.push("No booking changes");

  return (
    <div className="sticky bottom-0 z-20 flex items-center gap-3 rounded-card bg-ink px-4 py-2.5 text-inverse shadow-card">
      <span className="grid size-[34px] place-items-center rounded-[10px] bg-accent-soft text-[15px] font-extrabold text-ink tabular-nums">{changes}</span>
      <span className="grid min-w-0 flex-1 text-[12px]">
        <b className="text-[14px]">
          {changes} {changes === 1 ? "change" : "changes"} not published
          <span className="font-normal opacity-70"> · kept on this computer</span>
        </b>
        <span className="opacity-80 tabular-nums">{bits.join(". ")}.</span>
      </span>
      {problems ? (
        <span className="rounded-lg bg-late px-2.5 py-1 text-[12px] font-bold text-inverse">
          {problems} {problems === 1 ? "problem" : "problems"} to fix
        </span>
      ) : null}
      {undo}
      <Button variant="secondary" size="sm" onClick={onUndoAll}>
        Cancel
      </Button>
      <Button onClick={onReview}>Review and publish</Button>
    </div>
  );
}
